import React from 'react';
import { NumberBox } from 'devextreme-react/number-box';
import cs from '../../../utils/const.js';
import { resolveServiceRoutingHeaders } from '../../../utils/AntServiceRouting';
import { t, tf } from '../i18n';

// Resolve BE base URL + auth headers (same logic as BaseAPI.upload).
// Cần thiết vì fetch với URL tương đối sẽ gọi vào FE host, không phải BE.
function _resolveAPIContext() {
    var BaseURL = null;
    try { BaseURL = localStorage.getItem(cs.System_Code + '-Sys_BaseURL'); } catch (e) { /* ignore */ }
    if (!BaseURL || BaseURL === 'null' || BaseURL === 'undefined') BaseURL = cs.BaseURL;

    var token = '';
    var info = {};
    try { token = localStorage.getItem(cs.System_Code + '-session-token') || ''; } catch (e) { /* ignore */ }
    try {
        var raw = localStorage.getItem(cs.System_Code + '-session-info');
        if (raw) info = JSON.parse(raw) || {};
    } catch (e) { info = {}; }

    var headers = {
        Authorization: 'Bearer ' + token,
        'Business-Short-Code': info.business_short_code || '',
        'Business-Zone-Code': info.business_zone_code || '',
        'Business-Industry': info.business_industry || '',
        'Business-Sub-Industry': info.business_sub_industry || '',
        ...resolveServiceRoutingHeaders(info)
    };
    return { baseURL: BaseURL || '', headers: headers };
}

// Validate rows: trả { errors, rowErrors, validCount }
export function validateProducts(rows, locale) {
    var L = {
        nameRequired: t(locale, 'p_name_required'),
        nameTooLong: t(locale, 'p_name_too_long'),
        invalidPrice: t(locale, 'p_price_invalid'),
        noValidRow: t(locale, 'p_add_or_skip')
    };

    var rowErrors = {};
    var errors = [];
    var validCount = 0;

    rows.forEach(function (r, idx) {
        var name = String(r && r.name || '').trim();
        var priceStr = r && r.price != null ? String(r.price).trim() : '';
        if (!name && !priceStr) return; // empty — skip
        if (!name) { rowErrors[idx] = L.nameRequired; return; }
        if (name.length > 250) { rowErrors[idx] = L.nameTooLong; return; }
        if (priceStr) {
            var p = parseFloat(priceStr);
            if (!Number.isFinite(p) || p < 0) { rowErrors[idx] = L.invalidPrice; return; }
        }
        validCount++;
    });

    if (Object.keys(rowErrors).length > 0) {
        Object.keys(rowErrors).forEach(function (k) {
            var msg = rowErrors[k];
            if (errors.indexOf(msg) === -1) errors.push(msg);
        });
    }

    return { errors: errors, rowErrors: rowErrors, validCount: validCount };
}

var ALLOWED_MIMES = [
    'image/jpeg', 'image/jpg', 'image/png', 'image/webp',
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];
// Một số trình duyệt set type rỗng cho .xls/.csv/.doc — fallback theo extension.
var ALLOWED_EXT = ['.jpg', '.jpeg', '.png', '.webp', '.pdf', '.xls', '.xlsx', '.csv', '.doc', '.docx'];
var MAX_SIZE = 10 * 1024 * 1024;

function _hasAllowedExt(name) {
    if (!name) return false;
    var lower = String(name).toLowerCase();
    for (var i = 0; i < ALLOWED_EXT.length; i++) {
        if (lower.endsWith(ALLOWED_EXT[i])) return true;
    }
    return false;
}

export default class ProductTablePanel extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            uploading: false,
            generatingSamples: false,
            uploadError: null,
            uploadNote: null,
            dragOver: false
        };
        this.fileInputRef = React.createRef();
        this.samplePending = false;
        this.sampleMounted = true;
    }

    componentDidMount() { this.sampleMounted = true; }

    componentWillUnmount() {
        this.sampleMounted = false;
        if (this.sampleAbort) this.sampleAbort.abort();
    }

    update(idx, key, val) {
        var rows = (this.props.value && this.props.value.rows) || [];
        var next = rows.map(function (r, i) {
            return i === idx ? Object.assign({}, r, { [key]: val }) : r;
        });
        this.props.onChange({ ...this.props.value, rows: next });
    }

    add() {
        var rows = (this.props.value && this.props.value.rows) || [];
        var next = rows.concat([{ name: '', price: '', catalogue: '' }]);
        this.props.onChange({ ...this.props.value, rows: next });
    }

    remove(idx) {
        var rows = (this.props.value && this.props.value.rows) || [];
        this.props.onChange({ ...this.props.value, rows: rows.filter(function (_, i) { return i !== idx; }) });
    }

    async generateSamples() {
        if (!this.props.allowSampleData || this.samplePending || this.state.uploading || this.props.aiReady === false) return;
        this.samplePending = true;
        this.sampleAbort = new AbortController();
        var locale = this.props.locale || 'vi';
        this.setState({ uploading: true, generatingSamples: true, uploadError: null, uploadNote: null });
        try {
            var ctx = _resolveAPIContext();
            var response = await fetch((ctx.baseURL || '') + '/setting/SysSetupWizard/aiGenerateSampleProducts', {
                method: 'POST', credentials: 'include',
                signal: this.sampleAbort.signal,
                headers: { ...ctx.headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ productType: this.props.config.productType, locale: locale })
            });
            var json = await response.json();
            if (!this.sampleMounted) return;
            var payload = json && json.data ? json.data : json;
            if (!response.ok || !payload || !payload.ok) throw new Error(payload && payload.error || 'ai_failed');
            var current = (this.props.value && this.props.value.rows) || [];
            var seen = Object.create(null);
            current.forEach(function (r) { seen[String(r && r.name || '').trim().toLocaleLowerCase()] = true; });
            var next = current.slice();
            (payload.rows || []).forEach(function (r) {
                var name = String(r && r.name || '').trim();
                if (!name || seen[name.toLocaleLowerCase()]) return;
                seen[name.toLocaleLowerCase()] = true;
                next.push({ name: name, price: r.price || 0, catalogue: r.catalogue || '', _source: 'sample' });
            });
            this.props.onChange({ ...this.props.value, rows: next });
            this.setState({ uploading: false, generatingSamples: false, uploadNote: tf(locale, 'p_samples_added', { n: next.length - current.length }) });
        } catch (e) {
            if (!this.sampleMounted) return;
            var message = e.message === 'rate_limit_exceeded'
                ? t(locale, 'p_rate_limit')
                : e.message === 'ai_quota_or_provider_unavailable'
                    ? t(locale, 'p_ai_quota')
                    : t(locale, 'p_sample_failed');
            this.setState({ uploading: false, generatingSamples: false, uploadError: message });
        } finally {
            this.samplePending = false;
        }
    }

    handleFile(file) {
        if (this.samplePending) return;
        var locale = this.props.locale || 'vi';
        var T = {
            tooLarge: t(locale, 'p_file_too_large'),
            unsupported: t(locale, 'p_file_unsupported'),
            failed: t(locale, 'p_ai_read_failed')
        };

        if (!file) return;
        if (file.size > MAX_SIZE) {
            this.setState({ uploadError: T.tooLarge, uploadNote: null });
            return;
        }
        var mime = (file.type || '').toLowerCase();
        // Một số .xls/.csv không có MIME — fallback theo extension.
        if (ALLOWED_MIMES.indexOf(mime) < 0 && !_hasAllowedExt(file.name)) {
            this.setState({ uploadError: T.unsupported, uploadNote: null });
            return;
        }

        // KHÔNG truyền existingRows lên BE — AI chỉ bóc tách rows từ file.
        // Dedup so với rows hiện có trong state là việc của FE sau khi nhận response.
        var rows = (this.props.value && this.props.value.rows) || [];

        var fd = new FormData();
        fd.append('file', file);
        fd.append('productType', this.props.config.productType);

        var self = this;
        self.setState({ uploading: true, uploadError: null, uploadNote: null });
        var ep = this.props.aiSuggestEndpoint || '/setting/SysSetupWizard/aiSuggestProducts';
        var ctx = _resolveAPIContext();
        var queryURL = (ctx.baseURL || '') + ep;
        fetch(queryURL, {
            method: 'POST',
            body: fd,
            headers: ctx.headers,
            credentials: 'include'
        }).then(function (resp) {
            return resp.json();
        }).then(function (json) {
            // BE trả { data: { ok, rows, note } } — unwrap.
            // note/detail có thể là object đa ngôn ngữ { vi, en, ... } hoặc string.
            var payload = (json && json.data) ? json.data : json;
            var pickI18n = function (v) {
                if (v == null) return null;
                if (typeof v === 'string') return v;
                if (typeof v === 'object') return v[locale] || v.en || v.vi || Object.values(v)[0] || null;
                return String(v);
            };
            if (!payload || !payload.ok) {
                var errMsg = pickI18n(payload && payload.detail) || T.failed;
                self.setState({ uploading: false, uploadError: errMsg, uploadNote: null });
                return;
            }
            // Merge rows: skip dup tên (case-insensitive trim)
            var existingLower = {};
            rows.forEach(function (r) {
                var n = String(r && r.name || '').trim().toLowerCase();
                if (n) existingLower[n] = true;
            });
            var added = 0;
            var newRows = rows.slice();
            (payload.rows || []).forEach(function (r) {
                var n = String(r.name || '').trim();
                if (!n) return;
                if (existingLower[n.toLowerCase()]) return;
                newRows.push({
                    name: n,
                    price: r.price || 0,
                    catalogue: String(r.catalogue || '').trim(),
                    _source: 'ai'
                });
                added++;
            });
            self.props.onChange({ ...self.props.value, rows: newRows });
            self.setState({ uploading: false, uploadError: null, uploadNote: pickI18n(payload.note) || ('AI: ' + added) });
            // Reset file input để user có thể drop lại file mới
            if (self.fileInputRef.current) self.fileInputRef.current.value = '';
        }).catch(function () {
            self.setState({ uploading: false, uploadError: T.failed, uploadNote: null });
        });
    }

    onDrop(e) {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ dragOver: false });
        var files = e.dataTransfer && e.dataTransfer.files;
        if (files && files[0]) this.handleFile(files[0]);
    }

    onDragOver(e) {
        e.preventDefault();
        e.stopPropagation();
        if (!this.state.dragOver) this.setState({ dragOver: true });
    }

    onDragLeave(e) {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ dragOver: false });
    }

    onPickFile(e) {
        var files = e.target && e.target.files;
        if (files && files[0]) this.handleFile(files[0]);
    }

    onPaste(e) {
        // hỗ trợ paste ảnh từ clipboard
        var items = e.clipboardData && e.clipboardData.items;
        if (!items) return;
        for (var i = 0; i < items.length; i++) {
            if (items[i].kind === 'file') {
                var file = items[i].getAsFile();
                if (file) {
                    this.handleFile(file);
                    e.preventDefault();
                    return;
                }
            }
        }
    }

    render() {
        var locale = this.props.locale || 'vi';
        var rows = (this.props.value && this.props.value.rows) || [];
        var aiReady = this.props.aiReady !== false;
        var v = validateProducts(rows, locale);

        var L = {
            colName: t(locale, 'p_col_name'),
            colCatalogue: t(locale, 'p_col_group'),
            colPrice: t(locale, 'p_col_price'),
            addRow: '+ ' + t(locale, 'p_add_row'),
            dropHint: t(locale, 'p_drop_products'),
            uploadingMsg: t(locale, 'p_reading_file'),
            aiDisabled: t(locale, 'p_ai_disabled'),
            badgeAI: 'AI',
            pickFile: t(locale, 'p_pick_file'),
            sample: t(locale, 'p_sample'),
            sampleHint: t(locale, 'p_sample_hint'),
            generating: t(locale, 'p_generating')
        };

        var dropClass = 'wiz-products__dropzone';
        if (this.state.dragOver) dropClass += ' wiz-products__dropzone--over';
        if (!aiReady) dropClass += ' wiz-products__dropzone--disabled';
        if (this.state.uploading) dropClass += ' wiz-products__dropzone--uploading';

        return (
            <div className="wiz-products" onPaste={this.onPaste.bind(this)}>
                <div className="wiz-products__head">
                    <span className="wiz-products__col-no">#</span>
                    <span>{L.colName}</span>
                    <span>{L.colCatalogue}</span>
                    <span className="wiz-products__col-price">{L.colPrice}</span>
                    <span></span>
                </div>
                {rows.map(function (r, idx) {
                    var rowErr = v.rowErrors[idx];
                    var isAI = r && (r._source === 'ai' || r._source === 'sample');
                    var rowClass = 'wiz-products__row';
                    if (rowErr) rowClass += ' wiz-products__row--err';
                    if (isAI) rowClass += ' wiz-products__row--ai';
                    return (
                        <div className={rowClass} key={idx}>
                            <span className="wiz-products__col-no">{idx + 1}</span>
                            <input
                                className="wiz-products__input"
                                value={r.name || ''}
                                onChange={(function (i) { return function (e) { this.update(i, 'name', e.target.value); }.bind(this); }).bind(this)(idx)}
                            />
                            <input
                                className="wiz-products__input wiz-products__input--cat"
                                value={r.catalogue || ''}
                                placeholder={L.colCatalogue}
                                onChange={(function (i) { return function (e) { this.update(i, 'catalogue', e.target.value); }.bind(this); }).bind(this)(idx)}
                            />
                            <NumberBox
                                className="wiz-products__input wiz-products__input--num"
                                value={r.price === undefined || r.price === null || r.price === '' ? null : Number(r.price)}
                                format="#,##0"
                                min={0}
                                showSpinButtons={false}
                                onValueChanged={(function (i) { return function (e) { this.update(i, 'price', e.value == null ? '' : e.value); }.bind(this); }).bind(this)(idx)}
                            />
                            <button
                                type="button"
                                className="wiz-products__remove"
                                onClick={(function (i) { return function () { this.remove(i); }.bind(this); }).bind(this)(idx)}
                            >×</button>
                            {rowErr ? <div className="wiz-products__rowerr">{rowErr}</div> : null}
                        </div>
                    );
                }.bind(this))}
                <button type="button" className="wiz-products__add" onClick={this.add.bind(this)}>
                    {L.addRow}
                </button>

                {this.props.allowSampleData ? <div style={{ margin: '12px 0', padding: 12, borderRadius: 10, background: '#f2f6ff' }}>
                    <button type="button" className="wiz-products__add" disabled={!aiReady || this.state.uploading} onClick={this.generateSamples.bind(this)}>
                        ✨ AI · {this.state.generatingSamples ? L.generating : L.sample}
                    </button>
                    <div style={{ fontSize: 12, color: '#61718d', marginTop: 5 }}>{L.sampleHint}</div>
                    {['Food', 'Beverage', 'Good'].indexOf(this.props.config.productType) >= 0 ? <div style={{ fontSize: 12, color: '#61718d', marginTop: 5 }}>{t(locale, 'p_sample_images')}</div> : null}
                </div> : null}

                <div
                    className={dropClass}
                    onDrop={aiReady ? this.onDrop.bind(this) : undefined}
                    onDragOver={aiReady ? this.onDragOver.bind(this) : undefined}
                    onDragLeave={aiReady ? this.onDragLeave.bind(this) : undefined}
                    onClick={aiReady ? function () { this.fileInputRef.current && this.fileInputRef.current.click(); }.bind(this) : undefined}
                    title={aiReady ? '' : L.aiDisabled}
                >
                    <input
                        ref={this.fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.xls,.xlsx,.csv,.pdf,.doc,.docx"
                        style={{ display: 'none' }}
                        onChange={this.onPickFile.bind(this)}
                    />
                    {this.state.uploading ? (
                        <div className="wiz-products__dropzone-msg">
                            <div className="wiz-products__spinner" />
                            <div>{this.state.generatingSamples ? L.generating : L.uploadingMsg}</div>
                        </div>
                    ) : (
                        <div className="wiz-products__dropzone-msg">
                            <div className="wiz-products__dropzone-icon">⬇</div>
                            <div>{aiReady ? L.dropHint : L.aiDisabled}</div>
                            {aiReady ? <div className="wiz-products__dropzone-hint">{L.pickFile}</div> : null}
                        </div>
                    )}
                </div>

                {this.state.uploadError ? (
                    <div className="wiz-products__upload-err">{this.state.uploadError}</div>
                ) : null}
                {this.state.uploadNote ? (
                    <div className="wiz-products__upload-note">{this.state.uploadNote}</div>
                ) : null}
            </div>
        );
    }
}
